import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createInvoice } from '../services/api';
import { showErrorToast, showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError } from '../utils/toastConfig';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import './GenerateInvoice.css';

const SAC_CODES = {
  'Education': '999293',
  'Information Technology': '998313',
};

const DESCRIPTIONS = {
  'Education': 'Education Consultancy Services',
  'Information Technology': 'Information Technology and Consultancy Services',
};

const GenerateInvoice = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data } = await getClients();
        setClients(data);
      } catch (err) {
        console.error('Fetch clients error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setInvoiceForm({
      invoice_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: DESCRIPTIONS[client.sector],
    });
    setShowPopup(true);
    setError('');
  };

  const handleFormChange = (e) => {
    setInvoiceForm({ ...invoiceForm, [e.target.name]: e.target.value });
  };

  const calculateTax = () => {
    const amount = parseFloat(invoiceForm.amount) || 0;
    const isSameState = selectedClient?.state?.toLowerCase() === 'telangana';
    if (isSameState) {
      const cgst = amount * 0.09;
      const sgst = amount * 0.09;
      return { cgst, sgst, igst: 0, total: amount + cgst + sgst, type: 'CGST+SGST' };
    } else {
      const igst = amount * 0.18;
      return { cgst: 0, sgst: 0, igst, total: amount + igst, type: 'IGST' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceForm.amount || parseFloat(invoiceForm.amount) <= 0) {
      showErrorToast('Please enter a valid amount');
      setError('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    setError('');
    const toastId = showLoadingToast('Generating invoice...');
    
    try {
      const { data } = await createInvoice({
        client_id: selectedClient.id,
        invoice_date: invoiceForm.invoice_date,
        amount: invoiceForm.amount,
        description: invoiceForm.description,
      });
      updateLoadingToastSuccess(toastId, 'Invoice generated successfully! 📄');
      setTimeout(() => {
        navigate(`/invoice/${data.id}`);
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate invoice';
      setError(errorMsg);
      updateLoadingToastError(toastId, errorMsg);
      setSubmitting(false);
    }
  };

  const tax = calculateTax();

  return (
    <div className="page-container">
      <div className="page-card wide">
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          <FiArrowLeft /> Back to Dashboard
        </button>
        <h2>Generate Invoice</h2>
        <p className="subtitle">Select a client to generate an invoice</p>

        {loading ? (
          <p className="no-data">Loading clients...</p>
        ) : clients.length === 0 ? (
          <div className="no-data">
            <p>No clients found. Please add a client first.</p>
            <button className="btn-submit" style={{ width: 'auto', padding: '10px 24px' }} onClick={() => navigate('/add-client')}>
              Add Client
            </button>
          </div>
        ) : (
          <div className="client-list">
            {clients.map((client) => (
              <div key={client.id} className="client-card" onClick={() => handleSelectClient(client)}>
                <div className="client-info">
                  <h3>{client.client_name}</h3>
                  <p>{client.address}</p>
                  <span className="client-meta">
                    GST: {client.gst_number} | {client.state} | {client.sector}
                  </span>
                </div>
                <FiFileText size={20} className="client-icon" />
              </div>
            ))}
          </div>
        )}
      </div>

      {showPopup && selectedClient && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <h3>Generate Invoice for {selectedClient.client_name}</h3>
            <form onSubmit={handleSubmit}>
              {error && <div className="error-msg">{error}</div>}
              <div className="form-group">
                <label>Invoice Date *</label>
                <input
                  type="date"
                  name="invoice_date"
                  value={invoiceForm.invoice_date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Amount (&#8377;) *</label>
                <input
                  type="number"
                  name="amount"
                  value={invoiceForm.amount}
                  onChange={handleFormChange}
                  placeholder="Enter amount"
                  min="1"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <select name="description" value={invoiceForm.description} onChange={handleFormChange}>
                  <option value="Information Technology and Consultancy Services">
                    Information Technology and Consultancy Services
                  </option>
                  <option value="Education Consultancy Services">
                    Education Consultancy Services
                  </option>
                </select>
              </div>

              {invoiceForm.amount && parseFloat(invoiceForm.amount) > 0 && (
                <div className="tax-preview">
                  <h4>Tax Calculation Preview</h4>
                  <div className="tax-row">
                    <span>Base Amount:</span>
                    <span>&#8377;{parseFloat(invoiceForm.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {tax.type === 'CGST+SGST' ? (
                    <>
                      <div className="tax-row">
                        <span>CGST (9%):</span>
                        <span>&#8377;{tax.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="tax-row">
                        <span>SGST (9%):</span>
                        <span>&#8377;{tax.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="tax-row">
                      <span>IGST (18%):</span>
                      <span>&#8377;{tax.igst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="tax-row total">
                    <span>Total:</span>
                    <span>&#8377;{tax.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="tax-note">
                    {selectedClient.state.toLowerCase() === 'telangana'
                      ? 'Intra-state supply: CGST + SGST applicable'
                      : 'Inter-state supply: IGST applicable'}
                  </div>
                </div>
              )}

              <div className="popup-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowPopup(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateInvoice;
